import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { UnityCookieService } from '@utils/unity-cookie.service';
import { LoggingService } from '@logging/logging.service';
import { AuthenticationService } from '../authentication.service';
import { AuthState } from '@auth/state/models/auth.models';
import { TokenRefreshService } from '../token-refresh/token-refresh-service';
import { Log, LogType } from '@logging/log';
import { OnScreenMessagingService } from '@pattern-library/onscreen-messaging/onscreen-messaging.service';

const CONFIG_URL_PATTERNS = ['/assets/config/', 'ApplicationConfigSettings/'] as const;
export const REFRESH_FAILURE_MESSAGE = 'Token Refresh failed, redirecting to login';

const isConfigRequest = (method: string, url: string): boolean =>
  method === 'GET' && CONFIG_URL_PATTERNS.some((pattern) => url.includes(pattern));

const isUnauthenticatedRequest = (url: string): boolean =>
  url.includes('SignIn') || /refreshtoken/i.test(url);

const shouldBypassInterceptor = (method: string, url: string): boolean =>
  isConfigRequest(method, url) || isUnauthenticatedRequest(url);

export const CandCHttpInterceptor: HttpInterceptorFn = (req, next) => {
  if (shouldBypassInterceptor(req.method, req.url)) {
    return next(req);
  }

  const loggingService = inject(LoggingService);
  const authenticationService = inject(AuthenticationService);
  const unityCookieService = inject(UnityCookieService);
  const tokenRefreshService = inject(TokenRefreshService);
  const onscreenMessagingService = inject(OnScreenMessagingService);

  const createErrorLog = (error: unknown): Partial<Log> => {
    const requestId =
      error instanceof HttpErrorResponse ? error.headers.get('x-request-id') || undefined : undefined;

    return {
      type: LogType.Error,
      apiPath: req.url,
      apiVerb: req.method,
      responseTime: Date.now(),
      requestId
    };
  };

  const handleRefreshFailure = (): Observable<never> => {
    authenticationService.signOutAndRedirect();
    onscreenMessagingService.warn(REFRESH_FAILURE_MESSAGE);
    return throwError(() => new Error('Token refresh failed'));
  };

  const handleError = (error: unknown) => {
    if (error instanceof HttpErrorResponse && error.status === 401) {
      return tokenRefreshService.refreshActiveUser$().pipe(
        switchMap((response: AuthState | undefined) => {
          if (response?.success) {
            const newAuthToken = response.token ?? unityCookieService.getJWT();
            const newAuthReq = req.clone({
              setHeaders: { Authorization: `Bearer ${newAuthToken}` }
            });

            return next(newAuthReq);
          }

          return handleRefreshFailure();
        }),
        catchError((refreshError) => {
          loggingService.error('Error during token refresh', createErrorLog(refreshError));
          return handleRefreshFailure();
        })
      );
    }

    loggingService.error('HTTP request failed', createErrorLog(error));
    return throwError(() => error);
  };

  const authToken = unityCookieService.getJWT();
  const authReq = authToken
    ? req.clone({ setHeaders: { Authorization: `Bearer ${authToken}` } })
    : req;

  return next(authReq).pipe(catchError(handleError));
};